import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { PiLightbulb } from 'react-icons/pi'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { getEnergyModeNameMap } from '../../../services/energy-mode-config'
import { energyLevelTracker } from '../../../services/energyLevelTracking'

// Energy modes in order from lowest to highest
const ENERGY_MODES_ORDERED: EnergyMode[] = [EnergyMode.DRAINED, EnergyMode.STRUGGLING, EnergyMode.FUNCTIONAL, EnergyMode.ENERGIZED, EnergyMode.PEAK]

interface PreVacationCheckinProps {
  previousFeedbackMode?: EnergyMode | null
  vacationDates: string
  daysUntilLeave: number
  onEnergySelect?: (mode: EnergyMode) => void
}

export const PreVacationCheckin: React.FC<PreVacationCheckinProps> = ({ previousFeedbackMode = null, vacationDates, daysUntilLeave, onEnergySelect }) => {
  const { formatMessage } = useIntl()
  const energyModeNames = getEnergyModeNameMap(formatMessage)

  const previousIndex = previousFeedbackMode ? ENERGY_MODES_ORDERED.indexOf(previousFeedbackMode) : null

  const [selectedIndex, setSelectedIndex] = useState<number | null>(previousIndex)

  const handleSelect = (index: number) => {
    setSelectedIndex(index)
    const selectedMode = ENERGY_MODES_ORDERED[index]

    energyLevelTracker.trackEmptyStateCheckin({
      checkinType: 'pre_vacation',
      selectedEnergyMode: selectedMode,
    })

    if (onEnergySelect) {
      onEnergySelect(selectedMode)
    }
  }

  return (
    <div className="checkin-card checkin-card--pre-vacation">
      <div className="checkin-header-row">
        <div className="checkin-header">
          <span className="checkin-emoji" role="img" aria-label="palm tree">
            🌴
          </span>
          <h3 className="checkin-title">{formatMessage({ id: 'energyLevel.preVacation.title' })}</h3>
        </div>
        <div className="days-until-badge">
          <span className="badge-dot" />
          {formatMessage({ id: 'energyLevel.preVacation.daysAway' }, { days: daysUntilLeave })}
        </div>
      </div>
      <p className="checkin-description">{formatMessage({ id: 'energyLevel.preVacation.description' }, { dates: <strong key="dates">{vacationDates}</strong> })}</p>

      <div className="energy-selector">
        {ENERGY_MODES_ORDERED.map((mode, index) => {
          const isSelected = selectedIndex === index

          return (
            <button key={mode} type="button" onClick={() => handleSelect(index)} className={`energy-selector-button energy-${mode}${isSelected ? ' selected' : ''}`}>
              <div className="battery-icon">
                <div className="battery-tip" />
                <div className={`battery-fill fill-${mode}`} />
              </div>
              <span className="energy-label">{energyModeNames[mode]}</span>
            </button>
          )
        })}
      </div>

      <div className="checkin-notice">
        <PiLightbulb className="notice-icon" />
        <span>{formatMessage({ id: 'energyLevel.preVacation.recommendationsPaused' })}</span>
      </div>
    </div>
  )
}

export default PreVacationCheckin
